/**
 * EditableFieldList.tsx - Editable Field List with Draggr
 * 
 * CHANGE HISTORY:
 * - 251126_1756 [pending] danielbeer: Add editable field lists with Draggr
 * 
 * Provides editable field lists with:
 * - Draggr drag/drop integration
 * - Popover editor for adding/editing/deleting fields
 * - Array/list input support
 * - Reusable in Field Matcher and Visual Mapper
 */

import React, { useState, useCallback } from 'react';
import type { Field, FieldType } from '../types/index';
import { FieldEditorDialog } from './FieldEditorDialog';
import './FieldEditorDialog.css';

// Draggr will be optional - import if available
let useDraggable: any = null;
let useDroppable: any = null;
let DraggrProvider: any = null;
try {
  // @ts-ignore - optional dependency from dependr
  const dependr = require('@opcentral/dependr');
  useDraggable = dependr.useDraggable;
  useDroppable = dependr.useDroppable;
  DraggrProvider = dependr.DraggrProvider;
} catch (e) {
  // Draggr not available - drag disabled
  console.warn('[EditableFieldList] Draggr not available, drag/drop disabled');
}

export interface EditableFieldListProps {
  fields: Field[];
  type: 'source' | 'target';
  title: string;
  onFieldsChange: (fields: Field[]) => void;
  onFieldDragStart?: (field: Field) => void;
  onFieldDrop?: (field: Field) => void;
  getMappedStatus?: (fieldId: string) => boolean;
  showConfidence?: boolean;
  theme?: 'light' | 'dark';
  editable?: boolean;
  compact?: boolean;
  showFieldType?: boolean;
  showSampleData?: boolean;
}

/**
 * Draggable field item
 */
const DraggableField: React.FC<{
  field: Field;
  type: 'source' | 'target';
  isMapped: boolean;
  onDragStart?: (field: Field) => void;
  compact?: boolean;
  showFieldType?: boolean;
  showSampleData?: boolean;
}> = ({ field, type, isMapped, onDragStart, compact = false, showFieldType = true, showSampleData = true }) => {
  const draggableProps = useDraggable ? useDraggable(`field-${field.id}`, {
    data: { field, type },
    onDragStart: () => onDragStart?.(field)
  }) : {};

  return (
    <div
      {...draggableProps}
      className={`field-item ${isMapped ? 'field-item--mapped' : ''} ${compact ? 'field-item--compact' : ''}`}
      data-field-id={field.id}
      style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
    >
      <div className="field-item__header">
        <span className="field-item__icon">
          {isMapped ? '✓' : '○'}
        </span>
        <span className="field-item__name">
          {field.name}
          {field.required && <span className="field-item__required">*</span>}
        </span>
        {showFieldType && <span className="field-item__type">{field.type}</span>}
      </div>
      
      {!compact && field.description && (
        <div className="field-item__description">
          {field.description}
        </div>
      )}
      
      {showSampleData && field.sample && (
        <div className="field-item__sample">
          Sample: <code>{JSON.stringify(field.sample)}</code>
        </div>
      )}
    </div>
  );
};

// FieldEditorPopover removed - now using FieldEditorDialog

/**
 * Editable field list with drag/drop and popover editor
 */
export const EditableFieldList: React.FC<EditableFieldListProps> = ({
  fields,
  type,
  title,
  onFieldsChange,
  onFieldDragStart,
  onFieldDrop,
  getMappedStatus,
  showConfidence = true,
  theme = 'light',
  editable = true,
  compact = false,
  showFieldType: initialShowFieldType = true,
  showSampleData: initialShowSampleData = true
}) => {
  const [showEditor, setShowEditor] = useState(false);
  const [showFieldType, setShowFieldType] = useState(initialShowFieldType);
  const [showSampleData, setShowSampleData] = useState(initialShowSampleData);

  const droppableProps = useDroppable ? useDroppable(`field-list-${type}`, {
    onDrop: (dragData: any) => {
      if (dragData?.data?.field) {
        onFieldDrop?.(dragData.data.field);
      }
    }
  }) : {};

  const content = (
    <div className={`editable-field-list editable-field-list--${type} editable-field-list--${theme}`}>
      <div className="editable-field-list__header">
        <div className="editable-field-list__title-row">
          <h3>{title}</h3>
          <span className="editable-field-list__count">{fields.length}</span>
        </div>
        <div className="editable-field-list__controls">
          <button
            className="editable-field-list__toggle-btn"
            onClick={() => setShowFieldType(!showFieldType)}
            title="Toggle field types"
            data-active={showFieldType}
          >
            T
          </button>
          <button
            className="editable-field-list__toggle-btn"
            onClick={() => setShowSampleData(!showSampleData)}
            title="Toggle sample data"
            data-active={showSampleData}
          >
            S
          </button>
          {editable && (
            <button
              className="editable-field-list__edit-btn"
              onClick={() => setShowEditor(true)}
              title="Edit field list"
            >
              ✏️
            </button>
          )}
        </div>
      </div>

      <div {...droppableProps} className="editable-field-list__fields">
        {fields.length === 0 ? (
          <div className="editable-field-list__empty">
            <p>No fields defined</p>
            {editable && (
              <button onClick={() => setShowEditor(true)}>
                + Add Fields
              </button>
            )}
          </div>
        ) : (
          fields.map(field => (
            <DraggableField
              key={field.id}
              field={field}
              type={type}
              isMapped={getMappedStatus?.(field.id) || false}
              onDragStart={onFieldDragStart}
              compact={compact}
              showFieldType={showFieldType}
              showSampleData={showSampleData}
            />
          ))
        )}
      </div>

      {showEditor && (
        <FieldEditorDialog
          fields={fields}
          onSave={onFieldsChange}
          onClose={() => setShowEditor(false)}
          title={`Edit ${title}`}
        />
      )}
    </div>
  );

  // Wrap in DraggrProvider if available
  if (DraggrProvider) {
    return <DraggrProvider>{content}</DraggrProvider>;
  }
  
  return content;
};
